/**
 * Paid-click attribution shared by the deferred /campaign-track.js script,
 * the Gatsby bundle, and the /go/wa boot snippet.
 *
 * A tracking_id is created when a click id or utm param is first seen, then
 * reused for 90 days. A later page with no click ids and no utm must not open
 * a new campaign_trackings row. A new click id overwrites the stored one.
 */

export const TRACKING_ATTR_KEY = 'tracking_attr';
export const TRACKING_COOKIE = 'aa_track';
export const TRACKING_ID_KEY = 'tracking_id';
export const TRACKING_CLICK_KEY = 'tracking_click_key';
export const TRACKING_MAX_AGE_SEC = 90 * 24 * 60 * 60;
export const TRACKING_MAX_AGE_MS = TRACKING_MAX_AGE_SEC * 1000;

export const CLICK_ID_FIELDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'];
export const UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
];

export const gclidFromGclAw = (cookieString) => {
  const parts = String(cookieString || '').split(';');
  for (let i = 0; i < parts.length; i += 1) {
    const bit = parts[i].trim();
    if (!bit.startsWith('_gcl_aw=')) continue;
    let value = bit.slice('_gcl_aw='.length);
    try {
      value = decodeURIComponent(value);
    } catch (e) {
      // Keep the raw cookie value.
    }
    const segs = value.split('.');
    if (segs.length >= 3 && segs[0] === 'GCL') return segs.slice(2).join('.');
  }
  return '';
};

export const parseAttr = (raw, now = Date.now()) => {
  if (!raw) return null;
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!data || !data.id || !String(data.id).trim()) return null;
    if (data.exp && Number(data.exp) < now) return null;
    return {
      id: String(data.id).trim(),
      clickKey: data.clickKey || '',
      exp: data.exp ? Number(data.exp) : 0,
      gclid: data.gclid || '',
      gbraid: data.gbraid || '',
      wbraid: data.wbraid || '',
      fbclid: data.fbclid || '',
      msclkid: data.msclkid || '',
    };
  } catch (e) {
    return null;
  }
};

export const readCookieValue = (cookieString, name) => {
  const parts = String(cookieString || '').split(';');
  const prefix = `${name}=`;
  for (let i = 0; i < parts.length; i += 1) {
    const bit = parts[i].trim();
    if (!bit.startsWith(prefix)) continue;
    const raw = bit.slice(prefix.length);
    try {
      return decodeURIComponent(raw);
    } catch (e) {
      return raw;
    }
  }
  return '';
};

const attrIsExpired = (raw, now) => {
  if (!raw) return false;
  try {
    const data = JSON.parse(raw);
    return !!(data && data.id && data.exp && Number(data.exp) < now);
  } catch (e) {
    return false;
  }
};

/**
 * localStorage record, then the aa_track cookie, then a legacy tracking_id
 * that has no expiry yet. An expired record is not replaced by the legacy key.
 */
export const readStoredAttribution = ({ localStorage, cookie, now = Date.now() } = {}) => {
  let rawAttr = '';
  if (localStorage) {
    try {
      rawAttr = localStorage.getItem(TRACKING_ATTR_KEY) || '';
    } catch (e) {
      rawAttr = '';
    }
  }
  const fromStorage = parseAttr(rawAttr, now);
  if (fromStorage) return fromStorage;

  const fromCookie = parseAttr(readCookieValue(cookie, TRACKING_COOKIE), now);
  if (fromCookie) return fromCookie;

  if (attrIsExpired(rawAttr, now) || attrIsExpired(readCookieValue(cookie, TRACKING_COOKIE), now)) {
    return null;
  }

  if (!localStorage) return null;
  try {
    const id = localStorage.getItem(TRACKING_ID_KEY);
    if (!id || !String(id).trim()) return null;
    return {
      id: String(id).trim(),
      clickKey: localStorage.getItem(TRACKING_CLICK_KEY) || '',
      exp: 0,
      gclid: '',
      gbraid: '',
      wbraid: '',
      fbclid: '',
      msclkid: '',
    };
  } catch (e) {
    return null;
  }
};

export const buildAttributionRecord = (id, clickKey, clicks, now = Date.now()) => {
  const src = clicks || {};
  return {
    id: String(id).trim(),
    clickKey: clickKey || '',
    exp: now + TRACKING_MAX_AGE_MS,
    gclid: src.gclid || '',
    gbraid: src.gbraid || '',
    wbraid: src.wbraid || '',
    fbclid: src.fbclid || '',
    msclkid: src.msclkid || '',
    utm_source: src.utm_source || '',
    utm_medium: src.utm_medium || '',
    utm_campaign: src.utm_campaign || '',
    utm_term: src.utm_term || '',
    utm_content: src.utm_content || '',
    utm_id: src.utm_id || '',
  };
};

export const attributionCookie = (record) => (
  `${TRACKING_COOKIE}=${encodeURIComponent(JSON.stringify(record))}; Path=/; Max-Age=${TRACKING_MAX_AGE_SEC}; SameSite=Lax`
);

/**
 * @returns {{ action: 'post'|'reuse'|'skip', id?: string, reason?: string, gclid?: string }}
 */
export const attributionDecision = ({ clickKey, hasUtm, stored, gclAwGclid }) => {
  const fresh = !!(stored && stored.id);
  if (clickKey) {
    if (fresh && stored.clickKey === clickKey) return { action: 'reuse', id: stored.id };
    return { action: 'post', reason: 'click' };
  }
  if (hasUtm && !fresh) return { action: 'post', reason: 'utm' };
  if (fresh) return { action: 'reuse', id: stored.id };
  if (gclAwGclid) return { action: 'post', reason: 'gcl_aw', gclid: gclAwGclid };
  return { action: 'skip' };
};
